import { createApp } from "vue";
import { createWebHistory, createRouter } from "vue-router";

// styles

import "@fortawesome/fontawesome-free/css/all.min.css";
import "@/assets/styles/tailwind.css";

// mouting point for the whole app

import App from "@/App.vue";

// layouts

import Admin from "@/layouts/Admin.vue";
import Auth from "@/layouts/Auth.vue";
import AirportBoard from "@/views/admin/AirportBoard.vue";
import Configuration from "@/views/admin/Configuration.vue";
import UserManagement from "@/views/admin/UserManagement.vue";
import TextToSpeech from "@/views/admin/TextToSpeech.vue";
import Splash from "@/views/Splash.vue";
import Blocked from "@/views/Blocked.vue";
import Login from "@/views/auth/Login.vue";
import Register from "@/views/auth/Register.vue";
import Privacy from "@/views/Privacy.vue";
import Support from "@/views/Support.vue";
import Profile from "@/views/admin/Profile.vue";
import { isAuthenticated, getUser, refreshUser } from "@/lib/auth";
import { getGeoConfig, fetchGeoConfig } from "@/lib/geoConfig";

// routes

const routes = [
  {
    path: "/",
    component: Splash,
    meta: {
      title: "RespinaBoard - Flight Tracking & Airport Board",
      description: "Real-time flight tracking, airport search, live flight board, tour booking, hotel, train and bus reservations.",
    },
  },
  {
    path: "/blocked",
    component: Blocked,
    meta: {
      title: "Access Restricted | RespinaBoard",
      description: "This service is not available in your region.",
      robots: "noindex, nofollow",
    },
  },
  {
    path: "/auth",
    component: Auth,
    children: [
      {
        path: "login",
        component: Login,
        meta: {
          title: "Sign In | RespinaBoard",
          description: "Sign in to your RespinaBoard account. Track flights, save favorites and manage your travel.",
        },
      },
      {
        path: "register",
        component: Register,
        meta: {
          title: "Create Account | RespinaBoard",
          description: "Register for a free RespinaBoard account. Track flights, save favorite airports and view the live flight board.",
        },
      },
    ],
  },
  {
    path: "/airport-board",
    component: Admin,
    children: [{
      path: "",
      component: AirportBoard,
      meta: {
        title: "Flight Board | RespinaBoard",
        description: "Live flight board - search airports, track flights and monitor airline schedules in real-time.",
      },
    }],
  },
  {
    path: "/configuration",
    component: Admin,
    children: [{
      path: "",
      component: Configuration,
      meta: {
        title: "Configuration | RespinaBoard",
        description: "Manage application settings.",
        requiresAdmin: true,
        robots: "noindex, nofollow",
      },
    }],
  },
  {
    path: "/user-management",
    component: Admin,
    children: [{
      path: "",
      component: UserManagement,
      meta: {
        title: "User Management | RespinaBoard",
        description: "Manage user accounts.",
        requiresAdmin: true,
        robots: "noindex, nofollow",
      },
    }],
  },
  {
    path: "/text-to-speech",
    component: Admin,
    children: [{
      path: "",
      component: TextToSpeech,
      meta: {
        title: "Text to Speech | RespinaBoard",
        description: "Convert text into audio using ElevenLabs.",
        requiresAdminOrSupport: true,
        robots: "noindex, nofollow",
      },
    }],
  },
  {
    path: "/privacy",
    component: Admin,
    children: [{
      path: "",
      component: Privacy,
      meta: {
        title: "Privacy Policy | RespinaBoard",
        description: "RespinaBoard privacy policy. How we collect, use and protect your personal information.",
      },
    }],
  },
  {
    path: "/support",
    component: Admin,
    children: [{
      path: "",
      component: Support,
      meta: {
        title: "Support | RespinaBoard",
        description: "Get help with RespinaBoard. Contact support, FAQs, and information about the app.",
      },
    }],
  },
  {
    path: "/profile",
    component: Admin,
    children: [{
      path: "",
      component: Profile,
      meta: {
        title: "Account Settings | RespinaBoard",
        description: "Manage your RespinaBoard account settings.",
        robots: "noindex, nofollow",
      },
    }],
  },
  { path: "/admin", redirect: "/airport-board" },
  { path: "/admin/airport-board", redirect: "/airport-board" },
  { path: "/admin/configuration", redirect: "/configuration" },
  { path: "/admin/privacy", redirect: "/privacy" },
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  // Always allow splash/blocked pages
  if (to.path === "/" || to.path === "/blocked") {
    return next();
  }

  // Ensure geo config is loaded (baseUrl, etc.; isBlocked is for iOS app only)
  let cfg = getGeoConfig();
  if (!cfg) {
    try {
      cfg = await fetchGeoConfig();
    } catch {
      // If config fails, go to splash (shows error + retry)
      return next({ path: "/" });
    }
  }
  // isBlocked is for iOS app only; frontend does not block by geo.

  const authPages = ["/auth/login", "/auth/register"];
  const isAuthPage = authPages.some((p) => to.path.startsWith(p));

  // If already authenticated, avoid showing full-page auth screens.
  if (isAuthPage && isAuthenticated()) {
    return next({ path: "/airport-board" });
  }
  if (to.meta?.requiresAdmin) {
    // Admin-only routes still require authentication.
    if (!isAuthenticated()) {
      return next({ path: "/auth/login" });
    }
    const user = (await refreshUser()) || getUser();
    if (!user?.isAdmin) {
      return next({ path: "/airport-board" });
    }
  }
  if (to.meta?.requiresAdminOrSupport) {
    if (!isAuthenticated()) {
      return next({ path: "/auth/login" });
    }
    const user = (await refreshUser()) || getUser();
    if (!user?.isAdmin && !user?.isSupport) {
      return next({ path: "/airport-board" });
    }
  }
  next();
});

// SEO: Dynamic page title and meta tags per route
router.afterEach((to) => {
  const title = to.meta?.title || "RespinaBoard";
  document.title = title;

  const setMeta = (attr, key, content) => {
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };

  const desc = to.meta?.description || "Real-time flight tracking, airport search, live flight board, tour booking, hotel, train and bus reservations.";
  setMeta("name", "description", desc);

  const robots = to.meta?.robots || "index, follow";
  setMeta("name", "robots", robots);

  // Canonical URL
  const canonicalUrl = `https://respina-board.com${to.path}`;
  let canonicalEl = document.querySelector('link[rel="canonical"]');
  if (!canonicalEl) {
    canonicalEl = document.createElement("link");
    canonicalEl.setAttribute("rel", "canonical");
    document.head.appendChild(canonicalEl);
  }
  canonicalEl.setAttribute("href", canonicalUrl);

  // Open Graph
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", desc);
  setMeta("property", "og:url", canonicalUrl);

  // Twitter Card
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", desc);
  setMeta("name", "twitter:url", canonicalUrl);
});

createApp(App).use(router).mount("#app");
